// Wishlist functionality using localStorage

interface WishlistProduct {
  productId: string;
  productName: string;
  productBrand: string;
  productImage: string;
  productPrice?: number;
}

// Translation and language storage

let translations: Record<string, any> = {};

// Helper function to get translation
function getTranslation(key: string, fallback: string): string {
  
  // Try to get from stored translations
  if (translations[key]) {
    const translation = translations[key];
    return translation;
  }
  
  return fallback;
}

// Load and display wishlist products
async function loadWishlistProducts() {
  try {
    
    // Get wishlist from localStorage
    const wishlist: WishlistProduct[] = JSON.parse(localStorage.getItem('wishlist') || '[]');
    
    if (wishlist.length === 0) {
      showEmptyWishlist();
      return;
    }
    
    // Use the data directly from localStorage (no Firebase fetch needed)
    displayWishlistProducts(wishlist);
    
  } catch (error) {
    showError('Failed to load wishlist products');
  }
}

// Display wishlist products in both table and mobile card view
function displayWishlistProducts(products: WishlistProduct[]) {
  const tbody = document.querySelector('.wishlist-table tbody');
  const cardsContainer = document.querySelector('.wishlist-cards-row');
  
  if (!tbody && !cardsContainer) {
    return;
  }
  
  // Clear containers
  if (tbody) tbody.innerHTML = '';
  if (cardsContainer) cardsContainer.innerHTML = '';
  
  products.forEach((product) => {
    // Create table row for desktop
    if (tbody) {
      const row = createWishlistRow(product);
      tbody.appendChild(row);
    }
    
    // Create card for mobile
    if (cardsContainer) {
      const card = createWishlistCard(product);
      cardsContainer.appendChild(card);
    }
  });
  
  
  // Refresh translations after displaying products
  setTimeout(refreshWishlistTranslations, 100);
}

// Create a wishlist table row (desktop)
function createWishlistRow(product: WishlistProduct): HTMLTableRowElement {
  const row = document.createElement('tr');
  
  const productName = product.productName || 'Product';
  const productBrand = product.productBrand || 'N/A';
  const hasImage = product.productImage && product.productImage.trim() !== '';
  const productImage = hasImage ? product.productImage : null;
  const stockStatus = getTranslation('inStock', 'In Stock');
  
  
  // Create image HTML with proper fallback
  const imageHTML = hasImage 
    ? `<img src="${productImage}" alt="${productName}" loading="lazy" onerror="this.onerror=null; this.style.display='none'; this.nextElementSibling.style.display='flex';">
       <div class="product-icon-placeholder" style="display:none;">
         <i class="icon-tools"></i>
       </div>`
    : `<div class="product-icon-placeholder">
         <i class="icon-tools"></i>
       </div>`;
  
  row.innerHTML = `
    <td>
      <div class="product-box">
        <div class="cross-icon">
          <a href="#" class="remove-wishlist-item" data-product-id="${product.productId}">
            <i class="fas fa-times"></i>
          </a>
        </div>
        <div class="img-box">
          ${imageHTML}
        </div>
        <h3><a href="/produto/${product.productId}">${productName}</a></h3>
      </div>
    </td>
    <td>${productBrand}</td>
    <td class="stock-status-cell">${stockStatus}</td>
    <td>
      <div class="product-select">
        <a class="thm-btn wishlist-page__btn" href="/produto/${product.productId}">
          ${getTranslation('seeProduct', 'See the Product')} <span><i class="icon-next"></i></span>
        </a>
      </div>
    </td>
  `;
  
  return row;
}

// Create a wishlist card (mobile)
function createWishlistCard(product: WishlistProduct): HTMLDivElement {
  const card = document.createElement('div');
  card.className = 'wishlist-card';
  
  const productName = product.productName || 'Product';
  const productBrand = product.productBrand || 'N/A';
  const hasImage = product.productImage && product.productImage.trim() !== '';
  const productImage = hasImage ? product.productImage : null;
  const productPrice = product.productPrice ? `€${product.productPrice.toFixed(2)}` : '';
  const stockStatus = getTranslation('inStock', 'In Stock');
  
  
  // Create image HTML with proper fallback
  const imageHTML = hasImage 
    ? `<img src="${productImage}" alt="${productName}" loading="lazy" onerror="this.onerror=null; this.style.display='none'; this.nextElementSibling.style.display='flex';">
       <div class="product-icon-placeholder" style="display:none;">
         <i class="icon-tools"></i>
       </div>`
    : `<div class="product-icon-placeholder">
         <i class="icon-tools"></i>
       </div>`;
  
  card.innerHTML = `
    <div class="wishlist-card__remove">
      <a href="#" class="remove-wishlist-item" data-product-id="${product.productId}">
        <i class="fas fa-times"></i>
      </a>
    </div>
    <div class="wishlist-card__top">
      <div class="wishlist-card__image">
        <a href="/produto/${product.productId}">
          ${imageHTML}
        </a>
      </div>
      <div class="wishlist-card__content">
        <h3 class="wishlist-card__title">
          <a href="/produto/${product.productId}">${productName}</a>
        </h3>
        <div class="wishlist-card__details">
          <div class="wishlist-card__brand">
            <span class="wishlist-card__label">${getTranslation('brand', 'Brand')}:</span>
            <span class="wishlist-card__value">${productBrand}</span>
          </div>
          ${productPrice ? `
          <div class="wishlist-card__price">
            <span class="wishlist-card__price-value">${productPrice}</span>
          </div>
          ` : ''}
          <div class="wishlist-card__stock">
            <span class="wishlist-card__label">${getTranslation('stockStatus', 'Stock')}:</span>
            <span class="wishlist-card__stock-status stock-status-cell">${stockStatus}</span>
          </div>
        </div>
      </div>
    </div>
    <div class="wishlist-card__action">
      <a class="thm-btn wishlist-page__btn" href="/produto/${product.productId}">
        ${getTranslation('seeProduct', 'See the Product')} <span><i class="icon-next"></i></span>
      </a>
    </div>
  `;
  
  return card;
}

// Show empty wishlist message
function showEmptyWishlist() {
  const tbody = document.querySelector('.wishlist-table tbody');
  const cardsContainer = document.querySelector('.wishlist-cards-row');
  
  // Get translations
  const emptyTitle = getTranslation('emptyWishlist', 'Your wishlist is empty');
  const emptyMessage = getTranslation('emptyWishlistMessage', 'Add some products to your wishlist to see them here.');
  const browseText = getTranslation('browseProducts', 'Browse Products');
  
  const emptyContent = `
    <div class="empty-wishlist-message" style="text-align: center; padding: 40px;">
      <i class="far fa-heart empty-wishlist-icon" style="font-size: 48px; margin-bottom: 20px;"></i>
      <h3 class="empty-wishlist-title" style="margin-bottom: 10px;">${emptyTitle}</h3>
      <p class="empty-wishlist-text" style="margin-bottom: 20px;">${emptyMessage}</p>
      <a href="/loja" class="thm-btn empty-wishlist-btn">${browseText} <span><i class="icon-next"></i></span></a>
    </div>
  `;
  
  if (tbody) {
    tbody.innerHTML = `
      <tr>
        <td colspan="4">
          ${emptyContent}
        </td>
      </tr>
    `;
  }
  
  if (cardsContainer) {
    cardsContainer.innerHTML = emptyContent;
  }
  
  // Update colors based on theme
  updateEmptyWishlistTheme();
}

// Check if dark mode is active
function isDarkModeActive(): boolean {
  return document.documentElement.classList.contains('dark-mode') ||
         document.body.classList.contains('dark-mode') ||
         document.documentElement.classList.contains('dark-theme') ||
         document.body.classList.contains('dark-theme');
}

// Update empty wishlist theme colors
function updateEmptyWishlistTheme() {
  const isDarkMode = isDarkModeActive();
  const icon = document.querySelector('.empty-wishlist-icon') as HTMLElement;
  const title = document.querySelector('.empty-wishlist-title') as HTMLElement;
  const text = document.querySelector('.empty-wishlist-text') as HTMLElement;
  
  if (icon) {
    icon.style.color = isDarkMode ? '#666' : '#ccc';
  }
  if (title) {
    title.style.color = isDarkMode ? '#e0e0e0' : '#666';
  }
  if (text) {
    text.style.color = isDarkMode ? '#b0b0b0' : '#999';
  }
}

// Listen for theme changes
function setupThemeListener() {
  const observer = new MutationObserver(function(mutations) {
    mutations.forEach(function(mutation) {
      if (mutation.type === 'attributes' && mutation.attributeName === 'class') {
        updateEmptyWishlistTheme();
        updateWishlistTheme();
      }
    });
  });
  
  if (document.documentElement) {
    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ['class']
    });
  }
  if (document.body) {
    observer.observe(document.body, {
      attributes: true,
      attributeFilter: ['class']
    });
  }
  
  window.addEventListener('themeChanged', function() {
    setTimeout(() => {
      updateEmptyWishlistTheme();
      updateWishlistTheme();
    }, 100);
  });
  
  if ((window as any).ThemeContext) {
    const originalApplyTheme = (window as any).ThemeContext.applyTheme;
    (window as any).ThemeContext.applyTheme = function() {
      originalApplyTheme.call(this);
      setTimeout(() => {
        updateEmptyWishlistTheme();
        updateWishlistTheme();
      }, 50);
    };
  }
}

// Update wishlist page theme colors
function updateWishlistTheme() {
  const isDarkMode = isDarkModeActive();
  
  if (isDarkMode && !document.documentElement.classList.contains('dark-mode')) {
    document.documentElement.classList.add('dark-mode');
  } else if (!isDarkMode && document.documentElement.classList.contains('dark-mode')) {
    document.documentElement.classList.remove('dark-mode');
  }
}

// Remove item from wishlist with confirmation
function removeFromWishlist(productId: string) {
  
  const wishlist: WishlistProduct[] = JSON.parse(localStorage.getItem('wishlist') || '[]');
  
  const productToRemove = wishlist.find(item => item.productId === productId);
  
  const productName = productToRemove ? productToRemove.productName : getTranslation('thisProduct', 'this product');
  
  const dialogTitle = getTranslation('removeFromWishlistTitle', 'Remove from Wishlist');
  let dialogMessage = getTranslation('removeFromWishlistMessage', 'Are you sure you want to remove "{productName}" from your wishlist?');
  dialogMessage = dialogMessage.replace('{productName}', productName);
  const removeBtnText = getTranslation('removeButton', 'Remove');
  const cancelBtnText = getTranslation('cancelButton', 'Cancel');
  
  showConfirmationDialog(
    dialogTitle,
    dialogMessage,
    removeBtnText,
    cancelBtnText,
    () => {
      try {
        const currentWishlist: WishlistProduct[] = JSON.parse(localStorage.getItem('wishlist') || '[]');
        
        const updatedWishlist = currentWishlist.filter(item => item.productId !== productId);
        
        localStorage.setItem('wishlist', JSON.stringify(updatedWishlist));
        
        
        updateWishlistCounter();
        loadWishlistProducts();
        
        const successMessage = getTranslation('productRemovedSuccess', 'Product removed from wishlist');
        showMessage(successMessage, 'success');
        
      } catch (error) {
        const errorMessage = getTranslation('productRemovedError', 'Failed to remove product from wishlist');
        showMessage(errorMessage, 'error');
      }
    }
  );
}

// Update wishlist counter
function updateWishlistCounter() {
  const wishlist: WishlistProduct[] = JSON.parse(localStorage.getItem('wishlist') || '[]');
  const totalItems = wishlist.length;
  
  const wishlistCounter = document.querySelector('.main-menu__wishlist-count');
  if (wishlistCounter) {
    wishlistCounter.textContent = totalItems.toString();
  }
  
  window.dispatchEvent(new CustomEvent('wishlistUpdated', { 
    detail: { totalItems, wishlist } 
  }));
}

// Show error message
function showError(message: string) {
  const tbody = document.querySelector('.wishlist-table tbody');
  const cardsContainer = document.querySelector('.wishlist-cards-row');
  
  const errorContent = `
    <div style="text-align: center; padding: 40px; color: #dc3545;">
      <i class="fas fa-exclamation-triangle" style="font-size: 48px; margin-bottom: 20px;"></i>
      <h3>Error loading wishlist</h3>
      <p>${message}</p>
      <button onclick="location.reload()" class="thm-btn">Try Again</button>
    </div>
  `;
  
  if (tbody) {
    tbody.innerHTML = `<tr><td colspan="4">${errorContent}</td></tr>`;
  }
  if (cardsContainer) {
    cardsContainer.innerHTML = errorContent;
  }
}

// Show message to user
function showMessage(message: string, type: 'success' | 'error' | 'info') {
  let messageElement = document.getElementById('wishlist-message');
  if (!messageElement) {
    messageElement = document.createElement('div');
    messageElement.id = 'wishlist-message';
    messageElement.style.cssText = `
      position: fixed;
      top: 20px;
      right: 20px;
      padding: 15px 20px;
      border-radius: 5px;
      color: white;
      font-weight: bold;
      z-index: 10000;
      max-width: 300px;
      box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
    `;
    document.body.appendChild(messageElement);
  }
  
  messageElement.textContent = message;
  messageElement.style.backgroundColor = type === 'success' ? '#28a745' : 
                                        type === 'error' ? '#dc3545' : '#17a2b8';
  
  setTimeout(() => {
    if (messageElement && messageElement.parentNode) {
      messageElement.parentNode.removeChild(messageElement);
    }
  }, 3000);
}

// Custom confirmation dialog
function showConfirmationDialog(title: string, message: string, confirmText: string, cancelText: string, onConfirm: () => void) {
  const existingDialog = document.getElementById('custom-confirmation-dialog');
  if (existingDialog) {
    existingDialog.remove();
  }
  
  const overlay = document.createElement('div');
  overlay.id = 'custom-confirmation-dialog';
  overlay.style.cssText = `
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background-color: rgba(0, 0, 0, 0.5);
    display: flex;
    justify-content: center;
    align-items: center;
    z-index: 10001;
    animation: fadeIn 0.3s ease;
  `;
  
  const dialog = document.createElement('div');
  dialog.style.cssText = `
    background: white;
    border-radius: 12px;
    padding: 0;
    max-width: 400px;
    width: 90%;
    box-shadow: 0 20px 40px rgba(0, 0, 0, 0.15);
    animation: slideIn 0.3s ease;
    overflow: hidden;
  `;
  
  dialog.innerHTML = `
    <div style="padding: 30px 30px 20px 30px; text-align: center;">
      <div style="width: 60px; height: 60px; background: linear-gradient(135deg, #ff6b6b, #ee5a52); border-radius: 50%; margin: 0 auto 20px auto; display: flex; align-items: center; justify-content: center;">
        <i class="fas fa-heart-broken" style="color: white; font-size: 24px;"></i>
      </div>
      <h3 style="margin: 0 0 15px 0; color: #333; font-size: 20px; font-weight: 600;">${title}</h3>
      <p style="margin: 0 0 25px 0; color: #666; font-size: 16px; line-height: 1.5;">${message}</p>
    </div>
    <div style="padding: 0 30px 30px 30px; display: flex; gap: 12px; justify-content: center;">
      <button id="cancel-btn" style="
        background: #f8f9fa;
        color: #6c757d;
        border: 2px solid #e9ecef;
        padding: 12px 24px;
        border-radius: 8px;
        font-size: 14px;
        font-weight: 600;
        cursor: pointer;
        transition: all 0.2s ease;
        min-width: 100px;
      ">${cancelText}</button>
      <button id="confirm-btn" style="
        background: linear-gradient(135deg, #ff6b6b, #ee5a52);
        color: white;
        border: none;
        padding: 12px 24px;
        border-radius: 8px;
        font-size: 14px;
        font-weight: 600;
        cursor: pointer;
        transition: all 0.2s ease;
        min-width: 100px;
        box-shadow: 0 4px 12px rgba(255, 107, 107, 0.3);
      ">${confirmText}</button>
    </div>
  `;
  
  const style = document.createElement('style');
  style.textContent = `
    @keyframes fadeIn {
      from { opacity: 0; }
      to { opacity: 1; }
    }
    @keyframes slideIn {
      from { transform: translateY(-20px) scale(0.95); opacity: 0; }
      to { transform: translateY(0) scale(1); opacity: 1; }
    }
    #cancel-btn:hover {
      background: #e9ecef !important;
      border-color: #dee2e6 !important;
      transform: translateY(-1px);
    }
    #confirm-btn:hover {
      transform: translateY(-1px);
      box-shadow: 0 6px 16px rgba(255, 107, 107, 0.4) !important;
    }
    #confirm-btn:active, #cancel-btn:active {
      transform: translateY(0);
    }
  `;
  document.head.appendChild(style);
  
  overlay.appendChild(dialog);
  document.body.appendChild(overlay);
  
  const cancelBtn = document.getElementById('cancel-btn');
  const confirmBtn = document.getElementById('confirm-btn');
  
  const closeDialog = () => {
    overlay.style.animation = 'fadeOut 0.2s ease';
    setTimeout(() => {
      if (overlay.parentNode) {
        overlay.parentNode.removeChild(overlay);
      }
      if (style.parentNode) {
        style.parentNode.removeChild(style);
      }
    }, 200);
  };
  
  if (cancelBtn) {
    cancelBtn.addEventListener('click', closeDialog);
  }
  if (confirmBtn) {
    confirmBtn.addEventListener('click', () => {
      closeDialog();
      if (onConfirm) onConfirm();
    });
  }
  
  overlay.addEventListener('click', (e) => {
    if (e.target === overlay) {
      closeDialog();
    }
  });
  
  const handleEscape = (e: KeyboardEvent) => {
    if (e.key === 'Escape') {
      closeDialog();
      document.removeEventListener('keydown', handleEscape);
    }
  };
  document.addEventListener('keydown', handleEscape);
}

// Setup event listeners
function setupEventListeners() {
  document.addEventListener('click', function(e) {
    const target = e.target as HTMLElement;
    if (target.closest('.remove-wishlist-item')) {
      e.preventDefault();
      const removeButton = target.closest('.remove-wishlist-item') as HTMLElement;
      const productId = removeButton.getAttribute('data-product-id');
      if (productId) {
        removeFromWishlist(productId);
      }
    }
  });
}

// Function to refresh translations when language changes
function refreshWishlistTranslations() {
  const stockCells = document.querySelectorAll('.stock-status-cell');
  stockCells.forEach(cell => {
    const cellText = cell.textContent?.trim();
    if (cellText === 'In Stock' || cellText === 'Em Stock') {
      cell.textContent = getTranslation('inStock', 'In Stock');
    } else if (cellText === 'Out of Stock' || cellText === 'Fora de Stock') {
      cell.textContent = getTranslation('outOfStock', 'Out of Stock');
    }
  });
  
  const emptyTitle = document.querySelector('.empty-wishlist-title');
  const emptyText = document.querySelector('.empty-wishlist-text');
  const browseBtn = document.querySelector('.empty-wishlist-btn');
  
  if (emptyTitle) {
    emptyTitle.textContent = getTranslation('emptyWishlist', 'Your wishlist is empty');
  }
  if (emptyText) {
    emptyText.textContent = getTranslation('emptyWishlistMessage', 'Add some products to your wishlist to see them here.');
  }
  if (browseBtn) {
    const iconElement = browseBtn.querySelector('span');
    const icon = iconElement?.outerHTML || '';
    browseBtn.innerHTML = getTranslation('browseProducts', 'Browse Products') + ' ' + icon;
  }
  
  const selectBtns = document.querySelectorAll('.wishlist-page__btn');
  selectBtns.forEach(btn => {
    const iconElement = btn.querySelector('span');
    const icon = iconElement ? iconElement.outerHTML : '';
    const translatedText = getTranslation('seeProduct', 'See the Product');
    if (icon) {
      btn.innerHTML = translatedText + ' ' + icon;
    } else {
      btn.innerHTML = translatedText;
    }
  });
}

// Export initialization function that accepts language and translations
export function initializeWishlist(_lang: string = 'pt', trans: Record<string, any> = {}) {
  
  // Update the module-level language and translations
  translations = trans;
  
  loadWishlistProducts();
  setupEventListeners();
  setupThemeListener();
}

// Export function to update translations (for language changes)
export function updateWishlistTranslations(_lang: string, trans: Record<string, any>) {
  translations = trans;
  refreshWishlistTranslations();
}

